import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CasinoSettingKeyId, TrackingParams } from '@gains-com/shared';
import { DeviceService, DialogService, IconComponent } from '@gains-com/ui-angular';
import { WindowProvider } from '../../core/window.provider.js';
import { EnvironmentPipe } from '../../pipes/environment.pipe';
import { MetadataViewService } from '../../core/view-states/metadata-view.service';
import { LocalStorage } from '../../core/local-storage/local-storage.service';
import { LocalStorageKey } from '../../core/local-storage/local-storage-keys';
import { AnalyticsTrackingService } from '../../core/analytics-tracking.service';
import { AppInstallService } from '../../core/app-install.service.js';
import {
  AndroidInstallDialogComponent,
  AndroidInstallDialogData,
} from './android-install-dialog/android-install-dialog.component';

@Component({
  selector: 'app-install-banner',
  standalone: true,
  imports: [IconComponent, EnvironmentPipe],
  templateUrl: './app-install-banner.component.html',
  styleUrl: './app-install-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppInstallBannerComponent {
  private readonly _localStorage = inject(LocalStorage);
  private readonly _appInstall = inject(AppInstallService);
  private readonly _metadataView = inject(MetadataViewService);
  private readonly _analyticsTracking = inject(AnalyticsTrackingService);
  private readonly _dialog = inject(DialogService);
  private readonly _window = inject(WindowProvider);
  readonly deviceService = inject(DeviceService);

  private readonly _ready = signal(false);
  private readonly _dismissed = signal(false);
  /**
   * iOS banner gate (`app_install_banner` casino setting). Existing setting,
   * scoped semantically to iOS now that Android has its own toggle below.
   */
  private readonly _iosBannerEnabled = toSignal(
    this._metadataView.casinoSettingValue$<boolean>(CasinoSettingKeyId.AppInstallBanner),
  );

  /**
   * Android banner gate (`app_install_banner_android` casino setting).
   * Kill-switch — lets us ship the banner + `/android/click` plumbing to
   * production with the setting OFF so the Android variant stays hidden until
   * the APK release is ready, then flip it ON without a redeploy.
   */
  private readonly _androidBannerEnabled = toSignal(
    this._metadataView.casinoSettingValue$<boolean>(CasinoSettingKeyId.AppInstallBannerAndroid),
  );

  private readonly _platform = computed(() => {
    if (!this._ready()) {
      return null;
    }
    return this._appInstall.detectPlatform();
  });

  readonly shouldShowBanner = computed(() => {
    if (!this._ready() || !this.deviceService.isXsScreen() || this._dismissed()) {
      return false;
    }
    const platform = this._platform();
    if (platform === 'ios') {
      return !!this._iosBannerEnabled();
    }
    if (platform === 'android') {
      return !!this._androidBannerEnabled();
    }
    return false;
  });

  private readonly _pagePath = signal('');
  private readonly _trackingParams = signal<TrackingParams | null>(null);

  readonly installUrl = computed(() => {
    if (!this._ready()) {
      return AppInstallService.ONELINK_BASE_URL;
    }
    return this._appInstall.buildInstallUrl(
      this._platform(),
      this._pagePath() || '/',
      this._trackingParams(),
      'app_install_banner',
    );
  });

  constructor() {
    afterNextRender(() => {
      const wasDismissed = this._localStorage.getItem<boolean>(LocalStorageKey.GainsAppInstallBannerDismissed);
      if (wasDismissed) {
        this._dismissed.set(true);
      }
      this._pagePath.set(this._window.location.pathname);
      this._trackingParams.set(this._analyticsTracking.getParams());
      this._ready.set(true);
    });
  }

  /**
   * Intercepts the banner CTA on Android to warn about Chrome's "File might be
   * harmful" prompt before the sideload APK download starts. Android's install
   * URL is now an AppsFlyer OneLink that out-of-store-redirects to our
   * `/android/click` endpoint, so the `/android/click` target is
   * percent-encoded inside `af_android_url` and a URL substring check no longer
   * matches — we key off `_platform()` instead. The iOS OneLink path doesn't
   * trigger the warning, so those clicks pass through untouched.
   */
  onInstallClick(event: Event) {
    if (this._platform() !== 'android') return;
    const url = this.installUrl();

    event.preventDefault();
    this._dialog.open<unknown, AndroidInstallDialogData>(AndroidInstallDialogComponent, { data: { installUrl: url } });
  }

  onDismiss(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this._localStorage.setItem<boolean>(LocalStorageKey.GainsAppInstallBannerDismissed, true);
    this._dismissed.set(true);
  }
}
